import { saveAs } from "file-saver";

type FileSystemWritableFileStream = {
  write: (data: BlobPart) => Promise<void>;
  close: () => Promise<void>;
};

type FileSystemFileHandle = {
  createWritable: () => Promise<FileSystemWritableFileStream>;
};

type ShowSaveFilePicker = (options?: {
  suggestedName?: string;
  types?: Array<{
    description?: string;
    accept: Record<string, string[]>;
  }>;
}) => Promise<FileSystemFileHandle>;

declare global {
  interface Window {
    showSaveFilePicker?: ShowSaveFilePicker;
  }
}

export type SaveWithExportMarkArgs = {
  blob: Blob;
  filename: string;
  mimeType: string;
  extension: string;
  onSaved: () => Promise<void>;
};

/**
 * Saves a file and conditionally marks transactions as exported.
 *
 * Uses File System Access API (`showSaveFilePicker`) when available so a user
 * cancelling the browser save dialog skips the mark. Falls back to file-saver
 * `saveAs` on browsers without the API (Safari, Firefox) — there the mark
 * always fires because cancel is invisible to JS (documented limitation).
 */
export async function saveWithExportMark({
  blob,
  filename,
  mimeType,
  extension,
  onSaved,
}: SaveWithExportMarkArgs): Promise<void> {
  const picker = window.showSaveFilePicker;
  if (typeof picker === "function") {
    try {
      const handle = await picker({
        suggestedName: filename,
        types: [
          {
            accept: { [mimeType]: [`.${extension}`] },
          },
        ],
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      await onSaved();
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      throw error;
    }
    return;
  }

  saveAs(blob, filename);
  await onSaved();
}
